import Layout from '../../components/Layout';
import ProductComparison from '../../components/ProductComparison';
import DisclaimerNotice from '../../components/DisclaimerNotice';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight } from 'lucide-react';

// Vitamin D Comparison
const vitaminDProducts = [
  {
    name: "Vitamin D3 1000 IU",
    bestFor: "Maintenance",
    affiliateLink: "#",
    features: {
      dose: "1000 IU",
      form: "Softgel",
      absorption: "Good",
      thirdparty: true,
      vegfriendly: false,
      deficiency: "Mild support",
      frequency: "Daily",
      withfood: "Fat-containing meal"
    }
  },
  {
    name: "Vitamin D3 2000 IU",
    bestFor: "Low Levels",
    affiliateLink: "#",
    features: {
      dose: "2000 IU",
      form: "Softgel or drops",
      absorption: "Good",
      thirdparty: true,
      vegfriendly: false,
      deficiency: "Moderate support",
      frequency: "Daily",
      withfood: "Fat-containing meal"
    }
  },
  {
    name: "Vitamin D3 5000 IU",
    bestFor: "Deficiency",
    affiliateLink: "#",
    features: {
      dose: "5000 IU",
      form: "Softgel",
      absorption: "Good",
      thirdparty: true,
      vegfriendly: false,
      deficiency: "Strong support",
      frequency: "As directed",
      withfood: "Fat-containing meal"
    }
  }
];

const vitaminDFeatures = [
  { key: "dose", name: "Dosage" },
  { key: "form", name: "Form" },
  { key: "absorption", name: "Absorption" },
  { key: "thirdparty", name: "Third-Party Tested" },
  { key: "vegfriendly", name: "Vegan Friendly" },
  { key: "deficiency", name: "Deficiency Support" },
  { key: "frequency", name: "How Often" },
  { key: "withfood", name: "Best Taken With" }
];

// Magnesium Comparison
const magnesiumProducts = [
  {
    name: "Magnesium Glycinate",
    bestFor: "Sleep & Calm",
    affiliateLink: "#",
    features: {
      type: "Glycinate",
      absorption: "Excellent",
      gentle: true,
      sleep: true,
      muscle: true,
      energy: "Moderate",
      dosage: "200-400mg",
      timing: "Evening"
    }
  },
  {
    name: "Magnesium Citrate",
    bestFor: "Digestion",
    affiliateLink: "#",
    features: {
      type: "Citrate",
      absorption: "Good",
      gentle: false,
      sleep: false,
      muscle: true,
      energy: "Good",
      dosage: "200-400mg",
      timing: "With food"
    }
  },
  {
    name: "Magnesium L-Threonate",
    bestFor: "Brain Health",
    affiliateLink: "#",
    features: {
      type: "L-Threonate",
      absorption: "Excellent",
      gentle: true,
      sleep: true,
      muscle: false,
      energy: "Moderate",
      dosage: "Per label",
      timing: "Evening"
    }
  }
];

const magnesiumFeatures = [
  { key: "type", name: "Magnesium Type" },
  { key: "absorption", name: "Absorption Rate" },
  { key: "gentle", name: "Gentle on Stomach" },
  { key: "sleep", name: "Sleep Support" },
  { key: "muscle", name: "Muscle Support" },
  { key: "energy", name: "Energy Support" },
  { key: "dosage", name: "Typical Dosage" },
  { key: "timing", name: "Best Time to Take" }
];

// Omega-3 Comparison
const omegaProducts = [
  {
    name: "Standard Fish Oil",
    bestFor: "General Wellness",
    affiliateLink: "#",
    features: {
      source: "Fish (anchovy/sardine)",
      epa: "180mg per cap",
      dha: "120mg per cap",
      purity: "Good",
      burps: "Possible",
      vegan: false,
      servings: "2 caps/day",
      storage: "Refrigerate after opening"
    }
  },
  {
    name: "High-Potency Fish Oil",
    bestFor: "Targeted Support",
    affiliateLink: "#",
    features: {
      source: "Fish (concentrated)",
      epa: "500mg+ per cap",
      dha: "250mg+ per cap",
      purity: "Excellent",
      burps: "Minimal",
      vegan: false,
      servings: "1-2 caps/day",
      storage: "Refrigerate"
    }
  },
  {
    name: "Algae Omega-3",
    bestFor: "Vegans",
    affiliateLink: "#",
    features: {
      source: "Algae",
      epa: "Varies",
      dha: "250-500mg",
      purity: "Excellent",
      burps: "None",
      vegan: true,
      servings: "1-2 caps/day",
      storage: "Room temperature"
    }
  }
];

const omegaFeatures = [
  { key: "source", name: "Source" },
  { key: "epa", name: "EPA Content" },
  { key: "dha", name: "DHA Content" },
  { key: "purity", name: "Purity Level" },
  { key: "burps", name: "Fish Burps" },
  { key: "vegan", name: "Vegan" },
  { key: "servings", name: "Daily Serving" },
  { key: "storage", name: "Storage" }
];

const WellnessCompare = () => {
  return (
    <Layout>
      {/* Hero */}
      <section data-testid="wellness-compare-hero" className="py-20 md:py-32 bg-[#F2EBE3]">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <Link 
            to="/wellness" 
            className="inline-flex items-center gap-2 text-[#3A4D39] text-sm font-medium mb-6 hover:underline"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Wellness
          </Link>
          <p className="text-xs tracking-widest uppercase font-medium text-[#C88D74] mb-4">
            Product Comparison
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-[#1C1C1C] leading-tight mb-6">
            Compare Supplements
          </h1>
          <p className="text-base md:text-lg text-[#374151] leading-relaxed">
            Navigate the supplement aisle with confidence. Our comparisons break down the key differences to help you choose wisely.
          </p>
        </div>
      </section>

      {/* Vitamin D Comparison */}
      <section data-testid="vitamind-comparison" className="py-16 md:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="mb-8">
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#1C1C1C] mb-2">
              Vitamin D3
            </h2>
            <p className="text-[#374151]">
              Choose your dosage based on your current levels and healthcare provider guidance.
            </p>
          </div>
          <ProductComparison 
            title="Compare Features"
            products={vitaminDProducts}
            features={vitaminDFeatures}
          />
        </div>
      </section>

      {/* Magnesium Comparison */}
      <section data-testid="magnesium-comparison" className="py-16 md:py-24 bg-[#F5F5F0]">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="mb-8">
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#1C1C1C] mb-2">
              Magnesium Types
            </h2>
            <p className="text-[#374151]">
              Different forms serve different purposes—choose based on your goals.
            </p>
          </div>
          <ProductComparison 
            title="Compare Features"
            products={magnesiumProducts}
            features={magnesiumFeatures}
          />
        </div>
      </section>

      {/* Omega-3 Comparison */}
      <section data-testid="omega-comparison" className="py-16 md:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="mb-8">
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#1C1C1C] mb-2">
              Omega-3 Fatty Acids
            </h2>
            <p className="text-[#374151]">
              Fish oil vs. algae—find the right source for your needs.
            </p>
          </div>
          <ProductComparison 
            title="Compare Features"
            products={omegaProducts}
            features={omegaFeatures}
          />
        </div>
      </section>

      {/* Tips Section */}
      <section className="py-16 md:py-24 bg-[#F5F5F0]">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#1C1C1C] mb-6">
            Supplement Shopping Tips
          </h2>
          <ul className="space-y-4 text-[#374151]">
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 bg-[#3A4D39] rounded-full mt-2.5 flex-shrink-0" />
              <span><strong>Third-party testing:</strong> Look for USP, NSF, or ConsumerLab verification.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 bg-[#3A4D39] rounded-full mt-2.5 flex-shrink-0" />
              <span><strong>Check your levels:</strong> Blood tests reveal what you actually need.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 bg-[#3A4D39] rounded-full mt-2.5 flex-shrink-0" />
              <span><strong>Consult a professional:</strong> Talk to your doctor before starting supplements.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 bg-[#3A4D39] rounded-full mt-2.5 flex-shrink-0" />
              <span><strong>Quality over price:</strong> Cheap supplements may have fillers or poor absorption.</span>
            </li>
          </ul>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-12 bg-white">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <DisclaimerNotice />
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-20 bg-[#F2EBE3]">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <h3 className="font-heading text-2xl md:text-3xl font-semibold text-[#1C1C1C] mb-4">
            Explore more wellness guides
          </h3>
          <p className="text-[#374151] mb-6">
            From supplements to self-care, find practical recommendations for everyday wellbeing.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link 
              to="/wellness/supplements"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#3A4D39] text-white text-sm font-medium tracking-wide uppercase transition-all duration-300 hover:bg-[#2A3D29]"
            >
              Supplements Guide
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link 
              to="/wellness/self-care-essentials"
              className="inline-flex items-center gap-2 px-6 py-3 border border-[#3A4D39] text-[#3A4D39] text-sm font-medium tracking-wide uppercase transition-all duration-300 hover:bg-[#3A4D39] hover:text-white"
            >
              Self-Care Essentials
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default WellnessCompare;
